// Construction-plan calc engine v2 — D-1 core (blueprint §3).
// Vector statics + A-13(i) sheave friction. v1 riggingCalculations.ts stays
// untouched until D-6 cutover.

import type {
  LiftDetail, LiftGeometry, LiftHardware, LiftLoading, LiftComputed,
  WorkingEntry, Pt, Refusal,
} from '@/types/planData';

export const ENGINE_VERSION = '2.0.0';

// ---------------------------------------------------------------------------
// Core primitives (§3.2)
// ---------------------------------------------------------------------------

export function unit(a: Pt, b: Pt): Pt {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) throw new Error('R-7: zero-length segment');
  return { x: dx / len, y: dy / len };
}

export function angleBetweenDeg(u1: Pt, u2: Pt): number {
  const c = u1.x * u2.x + u1.y * u2.y;
  const m = Math.hypot(u1.x, u1.y) * Math.hypot(u2.x, u2.y);
  return Math.acos(Math.max(-1, Math.min(1, c / m))) * 180 / Math.PI;
}

export function resultantOfEqualTensions(T: number, phiDeg: number): number {
  return T * Math.sqrt(2 + 2 * Math.cos(phiDeg * Math.PI / 180));
}

export function sumVectors(vectors: { T: number; dir: Pt }[]): Pt {
  return vectors.reduce(
    (acc, { T, dir }) => ({ x: acc.x + T * dir.x, y: acc.y + T * dir.y }),
    { x: 0, y: 0 },
  );
}

export function thetaFromPoints(block: Pt, load: Pt): number {
  return Math.atan2(Math.abs(load.x - block.x), Math.abs(load.y - block.y)) * 180 / Math.PI;
}

// ---------------------------------------------------------------------------
// A-13(i) sheave bearing friction factors — verified table (source: ANSI/ASSP
// A10.48-2023 p.92). Bearing constants: PB=1.09, BB=1.045, SRB=1.02.
// SFF[S][bearing][N]: S = total reeved sheaves (1-10), N = line parts
// supporting the load (1-4). SFF = K^S · N / Σ(K^j, j=0..N-1) — reproduces the
// printed table exactly (verified digit-for-digit against the standard).
// ---------------------------------------------------------------------------

export type Bearing = 'PB' | 'BB' | 'SRB';

export const A13I_SFF: Record<number, Record<Bearing, Record<number, number>>> = (() => {
  const K: Record<Bearing, number> = { PB: 1.09, BB: 1.045, SRB: 1.02 };
  const t: Record<number, Record<Bearing, Record<number, number>>> = {};
  for (let S = 1; S <= 10; S++) {
    t[S] = { PB: {}, BB: {}, SRB: {} };
    for (const b of ['PB', 'BB', 'SRB'] as Bearing[]) {
      for (let N = 1; N <= 4; N++) {
        // S must be ≥ N (each of the N load-supporting parts reeves at least one sheave);
        // entries below that are unreachable per A-13(i).
        if (S >= N) {
          const gsum = Array.from({ length: N }, (_, j) => K[b] ** j).reduce((a, x) => a + x, 0);
          t[S][b][N] = Math.round((K[b] ** S * N / gsum) * 1000) / 1000;
        } else {
          t[S][b][N] = NaN;
        }
      }
    }
  }
  return t;
})();

// ---------------------------------------------------------------------------
// Friction reeving model (§3.1 A-1 fix / Brian override): through a friction
// sheave T_out = T_in × SFF; hoist side accumulates SFF across ALL reeved
// sheaves; the dead-end part carries tension in every block reaction.
// ---------------------------------------------------------------------------

export function resolveSFF(h: LiftHardware, parts: number): number {
  // frictionFactorPerSheave > 1 ⇒ explicit per-sheave factor; 1.0 ⇒ ideal toggle;
  // 0 (or unset) ⇒ use the embedded A-13(i) table.
  const f = h.frictionFactorPerSheave;
  if (f > 1) return f;
  if (f === 1) return 1;
  const S = h.sheaveCount;
  const b: Bearing = h.sheaveBearing === 'roller' ? 'SRB' : h.sheaveBearing === 'plain' ? 'PB' : 'PB';
  const v = A13I_SFF[Math.min(Math.max(S, 1), 10)]?.[b]?.[Math.min(Math.max(parts, 1), 4)];
  if (v == null || Number.isNaN(v)) return 1;
  return v;
}

export interface ReevingResult {
  deadEndLbs: number;   // dead-end part tension
  haulLbs: number;      // haul side after accumulating SFF across all reeved sheaves
  outBlockLbs: number;  // tension leaving the top block after friction
  winchPullLbs: number; // required line pull at the hoist
  sff: number;          // resolved SFF used
  working: WorkingEntry[];
}

/** Dead-end = load-side part tension; hoist side = dead end × SFF per sheave accumulated. */
export function reeveFriction(loadSideTensionLbs: number, sff: number, sheaveCount: number): ReevingResult {
  const deadEnd = loadSideTensionLbs;
  // haul side accumulates the product of SFF across all reeved sheaves
  const haul = deadEnd * Math.pow(sff, sheaveCount);
  const winchPull = haul;
  const out = deadEnd * Math.pow(sff, sheaveCount);
  const working: WorkingEntry[] = [
    {
      output: 'Dead-end part tension',
      formula: 'T_dead = T (load-side part tension)',
      substitution: `T = ${deadEnd.toFixed(1)} lb`,
      result: `${deadEnd.toFixed(1)} lb`,
      assumption: `Dead-end part carries load-side tension; SFF = ${sff} per sheave across ${sheaveCount} reeved sheave(s).`,
    },
    {
      output: 'Haul side tension (friction accumulation)',
      formula: 'T_haul = T_dead × SFF^S',
      substitution: `${deadEnd.toFixed(1)} × ${sff}^${sheaveCount}`,
      result: `${haul.toFixed(1)} lb`,
      assumption: 'Sheave friction accumulated per A-13(i) across every reeved sheave (Brian override — replaces ideal-MA default).',
    },
    {
      output: 'Required line pull at hoist',
      formula: 'winchPull = T_haul',
      substitution: `${haul.toFixed(1)} lb`,
      result: `${winchPull.toFixed(1)} lb`,
      assumption: 'Winch/capstan sits at the end of the haul side; pull equals accumulated haul tension.',
    },
  ];
  return { deadEndLbs: deadEnd, haulLbs: haul, outBlockLbs: out, winchPullLbs: winchPull, sff, working };
}

// ---------------------------------------------------------------------------
// ForceSet — the per-scenario force bundle
// ---------------------------------------------------------------------------

export interface ForceSet {
  linePartTensionLbs: number;
  topBlockResultantLbs: number;
  heelBlockLbs: number | null;
  redirectBlockLbs: number | null;      // S5
  mountBlockLbs: number | null;         // S5
  tieBackLbs: number | null;            // ALWAYS null (Z15: tie-back = tether catch, not tensioned member)
  tagLineLbs: number | null;
  slingLegLbs: number | null;
  winchPullLbs: number;
  structureReactions: {
    topLateralLbs: number; topVerticalLbs: number; baseMomentFtLbs: number;
    perAttachment: { name: string; horizontalLbs: number; verticalLbs: number; heightFt: number }[];
  };
}

// ---------------------------------------------------------------------------
// Shared force helpers
// ---------------------------------------------------------------------------

function W_eff(l: LiftLoading): number {
  return l.grossLoadLbs * l.dynamicFactor;
}

// ---------------------------------------------------------------------------
// R-7 shared input validation (§3.6) + Z8 DLF range — applied to EVERY
// scenario in computeLift BEFORE any force solve. A refused scenario emits NO
// numeric forces (status 'refused', zero/null ForceSet).
// ---------------------------------------------------------------------------

export interface ValidationIssue { rule: 'R-7'; message: string }

export function validateRiggingConfiguration(lift: LiftDetail): ValidationIssue[] {
  const g = lift.geometry;
  const issues: ValidationIssue[] = [];

  // Z8: DLF input range 1.0–2.0 — refuse outside (safety: refuse, not clamp).
  const dlf = lift.loading.dynamicFactor;
  if (!(dlf >= 1.0 && dlf <= 2.0)) {
    issues.push({
      rule: 'R-7',
      message: `DLF ${dlf} outside the allowed range 1.0–2.0 (Z8) — refused for safety; enter a dynamic factor between 1.0 and 2.0.`,
    });
  }

  const top: Pt = { x: g.topBlock.offsetFromCenterFt, y: g.topBlock.heightFt };
  const load: Pt = { x: g.load.distanceFromTowerFt, y: g.load.heightFt };

  // (a) load at/above the top block elevation — degenerate geometry.
  if (load.y >= top.y) {
    issues.push({
      rule: 'R-7',
      message: `Load at ${load.y} ft is at/above the top block elevation ${top.y} ft — degenerate geometry (§3.6 R-7 input validation error).`,
    });
  }

  // (b) θ ≥ 45° from vertical on the primary hoist line (Z18).
  const theta = thetaFromPoints(top, load);
  if (theta >= 45) {
    issues.push({
      rule: 'R-7',
      message: `Hoist-line angle θ = ${theta.toFixed(1)}° ≥ 45° from vertical — horizontal pull, not hoisting (§3.6 R-7 input validation error).`,
    });
  }

  // (b2) heel-block sanity (Luna audit 2026-09-09, probe-confirmed gaps):
  // heel must be BELOW the top block (at/above = degenerate: the rope goes up
  // past the block and comes back down, redirecting nothing) and on the SAME
  // side as the winch (opposite side = the winch→heel rope crosses the tower
  // at grade — only a jump-block (S3) arrangement may cross).
  if (g.heelBlock) {
    const hb = g.heelBlock;
    if (hb.heightFt >= g.topBlock.heightFt) {
      issues.push({
        rule: 'R-7',
        message: `Heel block at ${hb.heightFt} ft is at/above the top block (${g.topBlock.heightFt} ft) — degenerate: the hoist rope would rise past the block and return down, redirecting nothing. Place the heel below the top block.`,
      });
    }
    const wx0 = g.winch.distanceFromTowerFt;
    const hx = hb.offsetFromTowerFt;
    if (Math.abs(hx) > 1e-9 && Math.abs(wx0) > 1e-9 && Math.sign(hx) !== Math.sign(wx0)) {
      issues.push({
        rule: 'R-7',
        message: `Heel block (${hx} ft) is on the opposite side of the tower from the winch (${wx0} ft) — the winch-to-heel rope would cross the structure at grade. Only a jump-block (S3) arrangement crosses the tower.`,
      });
    }
  }

  // (c) zero-length segment: any two coincident geometry points.
  const pts: { name: string; p: Pt }[] = [
    { name: 'top block', p: top }, { name: 'load', p: load },
    { name: 'winch', p: { x: g.winch.distanceFromTowerFt, y: g.winch.heightFt } },
  ];
  if (g.heelBlock) pts.push({ name: 'heel block', p: { x: g.heelBlock.offsetFromTowerFt, y: g.heelBlock.heightFt } });
  if (g.jumpBlock) pts.push({ name: 'jump block', p: { x: g.jumpBlock.offsetFromCenterFt, y: g.jumpBlock.heightFt } });
  if (g.redirectBlock) pts.push({ name: 'redirect block', p: { x: g.redirectBlock.offsetFt, y: g.redirectBlock.heightFt } });
  if (g.mountBlock) pts.push({ name: 'mount block', p: { x: g.mountBlock.offsetFt, y: g.mountBlock.heightFt } });
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      // The S5/S6 mount IS the top-block attachment (same physical point) —
      // coincidence there is the intended geometry, not a zero-length segment.
      const isMountTopPair = (pts[i].name === 'top block' && pts[j].name === 'mount block')
        || (pts[i].name === 'mount block' && pts[j].name === 'top block');
      if (!isMountTopPair && Math.hypot(pts[i].p.x - pts[j].p.x, pts[i].p.y - pts[j].p.y) < 1e-9) {
        issues.push({
          rule: 'R-7',
          message: `Zero-length segment: ${pts[i].name} and ${pts[j].name} are coincident (§3.6 R-7 input validation error).`,
        });
      }
    }
  }

  // (d) winch inside the tower footprint (x < half face width, or negative distance).
  const wx = g.winch.distanceFromTowerFt;
  if (wx < 0) {
    issues.push({
      rule: 'R-7',
      message: `Winch at ${wx} ft is inside the tower footprint (negative distance) — §3.6 R-7 input validation error.`,
    });
  } else if (g.faceWidthFt != null && Math.abs(wx) < g.faceWidthFt / 2) {
    issues.push({
      rule: 'R-7',
      message: `Winch at ${wx} ft is inside the tower footprint (half face width ${g.faceWidthFt / 2} ft) — §3.6 R-7 input validation error.`,
    });
  }

  // (e) load-side rope must hang BELOW the pull-side rope (Brian 2026-09-08,
  // 'helium balloon' rule): at the block the load hangs from, the load-line
  // angle from vertical (θ_load) must be LESS than the pull-side angle
  // (θ_pull toward the winch / previous contact). If θ_load ≥ θ_pull the load
  // rope lies above the hoist rope — the block cannot hang in equilibrium and
  // the load would ride the pull side ('unless the load is a helium balloon').
  // Exemptions: S3-jump (the ONLY arrangement that legitimately crosses the
  // tower), S6-highline (load carried by trolley on the highline, not the
  // load line), and any geometry with a heel block — there the pull segment
  // leaves the top block toward the base (near-plumb) and the fleet-angle
  // rules R-6 already govern; the balloon comparison applies to the direct
  // winch-pull topology (S1 / S4 / S5) only.
  const hasHeel = !!g.heelBlock;
  if (lift.scenario !== 'S3-jump' && lift.scenario !== 'S6-highline-trolley') {
    const pullFrom: Pt = { x: g.winch.distanceFromTowerFt, y: g.winch.heightFt };
    // same-side check applies regardless of heel: load and winch on opposite
    // sides means the line crosses the structure — only a jump block does that.
    if (Math.sign(load.x || 1) !== Math.sign(pullFrom.x || 1) && Math.abs(load.x) > 1e-9 && Math.abs(pullFrom.x) > 1e-9) {
      issues.push({
        rule: 'R-7',
        message: `Load (${load.x} ft) and hoist (${pullFrom.x} ft) are on opposite sides of the tower — the load line would cross the structure. Only a jump-block (S3) arrangement crosses the tower.`,
      });
    } else if (!hasHeel) {
      // hang block: S5 the load hangs from the mount block; otherwise the top block
      const hangFrom: Pt = lift.scenario === 'S5-redirect' && g.mountBlock
        ? { x: g.mountBlock.offsetFt, y: g.mountBlock.heightFt }
        : top;
      const pullContact: Pt = lift.scenario === 'S5-redirect' && g.redirectBlock
        ? { x: g.redirectBlock.offsetFt, y: g.redirectBlock.heightFt }
        : pullFrom;
      const thetaLoad = thetaFromPoints(hangFrom, load);
      const thetaPull = thetaFromPoints(hangFrom, pullContact);
      if (thetaLoad >= thetaPull) {
        issues.push({
          rule: 'R-7',
          message: `Load line (${thetaLoad.toFixed(1)}° from vertical) is at/above the pull-side rope (${thetaPull.toFixed(1)}°) — the load would hang ABOVE the hoist rope, which rope cannot do. Lower the load, raise the block, or move the winch farther out.`,
        });
      }
    }
  }

  return issues;
}

/** Load equilibrium: R = W_eff / cos θ; T = R / n; tag = W_eff · tan θ. */
function loadEquilibrium(We: number, thetaDeg: number, n: number) {
  const th = thetaDeg * Math.PI / 180;
  const R = We / Math.cos(th);
  const T = R / n;
  const tag = We * Math.tan(th);
  return { R, T, tag };
}

/** 2-leg sling @α each side of vertical: leg = R / (2·cos α). α from VERTICAL (A-6).
 *  The legs carry the hook resultant R (W_eff/cos θ), not W_eff alone. */
function slingLeg(R: number, alphaDeg: number): number {
  return R / (2 * Math.cos(alphaDeg * Math.PI / 180));
}

// ---------------------------------------------------------------------------
// solveS1 — baseline top-block lift (§3.3)
// ---------------------------------------------------------------------------

export interface SolveS1Result {
  engineVersion: '2.0.0';
  derivedAngles: {
    loadPositionDeg: number;
    tagAngleDeg: number | null;
    fleetAngleDeg: number | null;
    slingAlphaDeg: number | null;
  };
  resultantR: number;
  forces: ForceSet;
  working: WorkingEntry[];
  refusals: { rule: string; message: string }[];
  advisories: { code: string; message: string }[];
  status: 'ok' | 'refused';
}

export function solveS1(lift: LiftDetail): SolveS1Result {
  const g = lift.geometry;
  const h = lift.hardware;
  const We = W_eff(lift.loading);
  const top: Pt = { x: g.topBlock.offsetFromCenterFt, y: g.topBlock.heightFt };
  const load: Pt = { x: g.load.distanceFromTowerFt, y: g.load.heightFt };

  const theta = thetaFromPoints(top, load);
  const { R, T, tag } = loadEquilibrium(We, theta, h.blockParts);

  // conservative (n+1)·T top block sizing, resolved on the load-line direction
  const topBlock = (h.blockParts + 1) * T;
  const topHoriz = topBlock * Math.sin(theta * Math.PI / 180);
  const topVert = topBlock * Math.cos(theta * Math.PI / 180);
  const baseMoment = topHoriz * g.topBlock.heightFt;

  // tag line: anchor on the SAME side as the winch/load (Brian field ruling 2026-09-08)
  const tagSign = Math.sign(g.load.distanceFromTowerFt || 1);
  const tagAnchor: Pt = { x: tagSign * (g.tag.anchorDistanceFt ?? 0), y: g.tag.anchorHeightFt ?? 0 };
  const tagAngleDeg = tagAnchor.y === 0 && tagAnchor.x === 0
    ? null
    : Math.atan2(Math.abs(load.x - tagAnchor.x), Math.abs(load.y - tagAnchor.y)) * 180 / Math.PI;

  // Block-attachment sling solve (Brian 2026-09-08):
  // - faceBasket2 (2 slings to 2 legs at panel points, block mid-face): α DERIVED
  //   from faceWidthFt + slingLengthFt via solveFaceBasket — never typed.
  // - default/1-leg: single sling straight down — leg tension = R, α = 0.
  // - legacy 2-leg @30° fixture preserved via blockAttachment === '2-leg'
  //   WITHOUT face-basket dimensions (keeps S1-A regression fixture green).
  const att = g.topBlock.attachment;
  let alphaDeg: number | null = 30;
  let leg = slingLeg(R, 30);
  let basketWorking: WorkingEntry[] = [];
  let basketWarn = false;
  if (att.method === 'faceBasket2' && att.faceWidthFt != null && att.slingLengthFt != null && att.faceWidthFt > 0 && att.slingLengthFt > 0) {
    const sol = solveFaceBasket(topBlock, att.faceWidthFt, att.slingLengthFt);
    alphaDeg = sol.alphaDeg;
    leg = sol.legLbs;
    basketWorking = sol.working;
    basketWarn = sol.warn;
  } else if (att.method !== 'faceBasket2' && lift.hardware.blockAttachment === '1-leg') {
    alphaDeg = 0;
    leg = R;
  }

  const working: WorkingEntry[] = [
    {
      output: 'Load position angle θ',
      formula: 'θ = atan2(|Δx|, Δy)',
      substitution: `atan2(${Math.abs(load.x - top.x).toFixed(1)} ft, ${Math.abs(load.y - top.y).toFixed(1)} ft)`,
      result: `${theta.toFixed(2)}°`,
      assumption: 'Angle derived from stored point coordinates (PRD D9 — never typed).',
    },
    {
      output: 'Effective load W_eff',
      formula: 'W_eff = gross × DLF',
      substitution: `${lift.loading.grossLoadLbs} × ${lift.loading.dynamicFactor}`,
      result: `${We.toFixed(1)} lb`,
      assumption: `DLF = ${lift.loading.dynamicFactor} (capstan start advisories handled separately).`,
    },
    {
      output: 'Load-line resultant R',
      formula: 'R = W_eff / cos θ',
      substitution: `${We.toFixed(1)} / cos ${theta.toFixed(2)}°`,
      result: `${R.toFixed(1)} lb`,
      assumption: 'Single-source-of-truth equilibrium (A-2 fix — no second hoist-load quantity).',
    },
    {
      output: `Line part tension T (${h.blockParts} parts)`,
      formula: 'T = R / n',
      substitution: `${R.toFixed(1)} / ${h.blockParts}`,
      result: `${T.toFixed(1)} lb`,
      assumption: 'Equal-part ideal load distribution (friction path handled by solveReeving).',
    },
    {
      output: 'Top block resultant (conservative sizing)',
      formula: 'Top = (n+1)·T',
      substitution: `(${h.blockParts}+1) × ${T.toFixed(1)}`,
      result: `${topBlock.toFixed(1)} lb`,
      assumption: 'Conservative parallel-parts model per A-1 fix — exact vector model used where segments diverge (S2/S3/S5).',
    },
    {
      output: 'Structure reaction at top block',
      formula: 'H = R·sin θ, V = R·cos θ',
      substitution: `${R.toFixed(1)} × sin ${theta.toFixed(2)}°, ${R.toFixed(1)} × cos ${theta.toFixed(2)}°`,
      result: `H = ${topHoriz.toFixed(1)} lb, V = ${topVert.toFixed(1)} lb`,
      assumption: `Conservative (n+1)·T resultant resolved at attachment elevation ${g.topBlock.heightFt} ft.`,
    },
    {
      output: 'Base moment from top block lateral',
      formula: 'M = H × height',
      substitution: `${topHoriz.toFixed(1)} × ${g.topBlock.heightFt}`,
      result: `${baseMoment.toFixed(1)} ft·lb`,
      assumption: 'Moment from horizontal component × attachment height.',
    },
    {
      output: 'Tag line force',
      formula: 'tag = W_eff · tan θ',
      substitution: `${We.toFixed(1)} × tan ${theta.toFixed(2)}°`,
      result: `${tag.toFixed(1)} lb`,
      assumption: 'Straight tag terminated to the load; anchor opposite the load side (A-5 fix — derived angle).',
    },
    // sling leg working entry — reflects the selected block attachment
    ...(att.method === 'faceBasket2'
      ? basketWorking
      : alphaDeg === 0
        ? [{
            output: 'Sling tension (single sling, vertical)',
            formula: 'leg = R',
            substitution: `${R.toFixed(1)}`,
            result: `${leg.toFixed(1)} lb`,
            assumption: 'Single sling from block straight down to the load — no angle multiplier.',
          }]
        : [{
            output: 'Sling leg tension (2-leg @30° from vertical)',
            formula: 'leg = R / (2·cos α)',
            substitution: `${R.toFixed(1)} / (2 × cos ${alphaDeg}°)`,
            result: `${leg.toFixed(1)} lb`,
            assumption: 'α measured from VERTICAL (A-6 convention fix); symmetric 2-leg sling carrying the hook resultant R.',
          }]),
  ];

  // Static-tag anchor validation (FIX 1 companion): anchor must be above the
  // load destination elevation; warn when below the top block. Fires only for
  // staticTrolley tag method, where the static line exists.
  const refusals: { rule: string; message: string }[] = [];
  const advisories: { code: string; message: string }[] = [];
  if (att.method === 'faceBasket2') {
    const fb = solveFaceBasket(topBlock, att.faceWidthFt ?? 1, att.slingLengthFt ?? 1);
    if (fb.refusal) refusals.push({ rule: fb.refusal.rule, message: fb.refusal.message });
    else if (fb.warn) advisories.push({
      code: 'R-1-ADVISORY',
      message: `Face-basket sling angle α = ${fb.alphaDeg.toFixed(1)}° from vertical exceeds 45° — consider longer slings or narrower face spacing.`,
    });
  }
  if (g.tag.method === 'staticTrolley') {
    const anchorY = g.tag.staticUpperAnchorHeightFt;
    if (anchorY != null && anchorY <= load.y) {
      refusals.push({
        rule: 'R-7',
        message: `Static upper anchor at ${anchorY} ft is not above the load destination elevation ${load.y} ft — static-tag input rule (PRD): anchor must sit above where the load will land.`,
      });
    }
    if (anchorY != null && anchorY < g.topBlock.heightFt) {
      advisories.push({
        code: 'R-6-ADVISORY',
        message: `Static upper anchor at ${anchorY} ft is ${(g.topBlock.heightFt - anchorY).toFixed(1)} ft below the top block (${g.topBlock.heightFt} ft) — check the anchor attachment.`,
      });
    }
  }

  return {
    engineVersion: '2.0.0',
    derivedAngles: { loadPositionDeg: theta, tagAngleDeg, fleetAngleDeg: null, slingAlphaDeg: alphaDeg },
    resultantR: R,
    forces: {
      linePartTensionLbs: T,
      topBlockResultantLbs: topBlock,
      heelBlockLbs: null,
      redirectBlockLbs: null,
      mountBlockLbs: null,
      tieBackLbs: null,
      tagLineLbs: tag,
      slingLegLbs: leg,
      winchPullLbs: T,
      structureReactions: {
        topLateralLbs: topHoriz,
        topVerticalLbs: topVert,
        baseMomentFtLbs: baseMoment,
        perAttachment: [
          { name: 'top block', horizontalLbs: topHoriz, verticalLbs: topVert, heightFt: g.topBlock.heightFt },
        ],
      },
    },
    working,
    refusals,
    advisories,
    status: refusals.length > 0 ? 'refused' : 'ok',
  };
}

/** Empty ForceSet for refused scenarios — NO numeric forces. */
export const EMPTY_FORCES: ForceSet = {
  linePartTensionLbs: 0,
  topBlockResultantLbs: 0,
  heelBlockLbs: null,
  redirectBlockLbs: null,
  mountBlockLbs: null,
  tieBackLbs: null,
  tagLineLbs: null,
  slingLegLbs: null,
  winchPullLbs: 0,
  structureReactions: {
    topLateralLbs: 0, topVerticalLbs: 0, baseMomentFtLbs: 0, perAttachment: [],
  },
};

// ---------------------------------------------------------------------------
// solveS2 — heel block vector resultant (§3.3, A-3 fix)
// ---------------------------------------------------------------------------

export interface SolveS2Result extends SolveS1Result {
  phiDeg: number;
  fleetAngleDeg: number;
}

export function solveS2(lift: LiftDetail): SolveS2Result {
  const base = solveS1(lift);
  const g = lift.geometry;
  const T = base.forces.linePartTensionLbs;

  const top: Pt = { x: g.topBlock.offsetFromCenterFt, y: g.topBlock.heightFt };
  const heel: Pt = { x: g.heelBlock!.offsetFromTowerFt, y: g.heelBlock!.heightFt };
  const winch: Pt = { x: g.winch.distanceFromTowerFt, y: g.winch.heightFt };

  const uTopHeel = unit(top, heel);
  const uHeelTop = unit(heel, top);
  const uHeelWinch = unit(heel, winch);

  // fleet angle = deviation of the top→heel segment from vertical (A-13(k))
  const fleetDeg = Math.atan2(Math.abs(uTopHeel.x), Math.abs(uTopHeel.y)) * 180 / Math.PI;

  // φ = angle between the two directions LEAVING the heel (heel→top, heel→winch)
  const phi = angleBetweenDeg(uHeelTop, uHeelWinch);
  const heelResultant = resultantOfEqualTensions(T, phi);

  // heel anchor reactions = H/V components of the EXACT vector sum of the two
  // segment tensions pulling away from the heel
  const vecSum = sumVectors([
    { T, dir: uHeelTop },
    { T, dir: uHeelWinch },
  ]);
  const anchorH = Math.abs(vecSum.x);
  const anchorV = Math.abs(vecSum.y);
  const baseMoment2 = base.forces.structureReactions.baseMomentFtLbs + anchorH * g.heelBlock!.heightFt;

  const working: WorkingEntry[] = [
    ...base.working,
    {
      output: 'Fleet angle (top→heel vs heel→winch)',
      formula: 'fleet = angle(u₁, u₂)',
      substitution: `angleBetween([${uTopHeel.x.toFixed(4)}, ${uTopHeel.y.toFixed(4)}], [${uHeelWinch.x.toFixed(4)}, ${uHeelWinch.y.toFixed(4)}])`,
      result: `${fleetDeg.toFixed(2)}°`,
      assumption: 'Derived from stored coordinates; advisory >2°, refusal >5° (R-6).',
    },
    {
      output: 'Heel block resultant',
      formula: 'F = T·√(2 + 2·cos φ)',
      substitution: `${T.toFixed(1)} × √(2 + 2·cos ${phi.toFixed(2)}°)`,
      result: `${heelResultant.toFixed(1)} lb`,
      assumption: 'Vector resultant at the heel from segment directions (A-3 fix — replaces v1 lineLoad×0.5).',
    },
    {
      output: 'Heel anchor reactions (H+V)',
      formula: 'H = F·|û_x|, V = F·|û_y|',
      substitution: `${heelResultant.toFixed(1)} lb resolved on top→heel direction`,
      result: `H = ${anchorH.toFixed(1)} lb, V = ${anchorV.toFixed(1)} lb`,
      assumption: `Heel attachment at ${g.heelBlock!.heightFt} ft; H adds to base moment.`,
    },
    {
      output: 'Base moment (top + heel)',
      formula: 'M = H_top×h_top + H_heel×h_heel',
      substitution: `${base.forces.structureReactions.topLateralLbs.toFixed(1)}×${g.topBlock.heightFt} + ${anchorH.toFixed(1)}×${g.heelBlock!.heightFt}`,
      result: `${baseMoment2.toFixed(1)} ft·lb`,
      assumption: 'Heel lateral adds H_anchor × heel height to the base moment.',
    },
  ];

  return {
    ...base,
    phiDeg: phi,
    fleetAngleDeg: fleetDeg,
    derivedAngles: { ...base.derivedAngles, fleetAngleDeg: fleetDeg },
    forces: {
      ...base.forces,
      heelBlockLbs: heelResultant,
      structureReactions: {
        topLateralLbs: base.forces.structureReactions.topLateralLbs,
        topVerticalLbs: base.forces.structureReactions.topVerticalLbs,
        baseMomentFtLbs: baseMoment2,
        perAttachment: [
          ...base.forces.structureReactions.perAttachment,
          { name: 'heel block', horizontalLbs: anchorH, verticalLbs: anchorV, heightFt: g.heelBlock!.heightFt },
        ],
      },
    },
    working,
  };
}

// ---------------------------------------------------------------------------
// solveReeving — multi-part reeving incl. dead-end (S-2PART-1 path)
// ---------------------------------------------------------------------------

export interface SolveReevingResult extends SolveS2Result {
  tensions: { deadEndLbs: number; haulLbs: number; outBlockLbs: number };
  ideal: boolean;
}

export function solveReeving(lift: LiftDetail): SolveReevingResult {
  const base = solveS2(lift);
  const h = lift.hardware;
  const ideal = h.frictionFactorPerSheave === 1.0;
  const n = h.blockParts;
  // A-13(i) table is the SINGLE SOURCE OF TRUTH: K is read from the table
  // (K = A13I_SFF[1][bearing][1] — the S=1 entry IS the per-sheave constant),
  // and the winch pull is recovered through the tabulated multiplier
  // A13I_SFF[S][b][N] = K^S · N / Σ(K^j, j=0..N-1), so
  //   T_dead     = R / Σ(K^j, j=0..N-1)
  //   winchPull  = T_dead × K^S = T_dead × A13I_SFF[S][b][N] × Σ(K^j)/N
  // S semantics (EXPLICIT): S = TOTAL sheave passes between the dead-end part
  // and the winch, INCLUDING the heel/fleet block. For a 2-part line reeved
  // over load block + top block + heel block, S = 3.
  const b: Bearing = h.sheaveBearing === 'roller' ? 'SRB' : 'PB';
  const S = Math.min(Math.max(h.sheaveCount, 1), 10);
  const N = Math.min(Math.max(n, 1), 4);
  const K = A13I_SFF[1][b][1];
  const gsum = Array.from({ length: N }, (_, j) => K ** j).reduce((a, x) => a + x, 0);
  const tabulated = A13I_SFF[S][b][N];
  const kPowS = Number.isNaN(tabulated) ? K ** S : tabulated * (gsum / N); // = K^S via the table

  // Load equilibrium at the load: R = W_eff/cos θ distributed across the parts.
  // Friction makes the parts UNEQUAL: dead-end part = R/Σ(K^j, j=0..N-1);
  // the haul side then accumulates ×K per reeved sheave pass: dead → haul →
  // out → winch (verified against A-13(i)/S-2PART-1).
  const We = W_eff(lift.loading);
  const theta = base.derivedAngles.loadPositionDeg;
  const R = We / Math.cos(theta * Math.PI / 180);
  const deadEnd = ideal ? We / n : R / gsum;
  const haul = ideal ? deadEnd : deadEnd * K;
  const outBlock = ideal ? deadEnd : haul * K;
  const winchPull = ideal ? We / n : deadEnd * kPowS;

  // EXACT vector sum at the top block: load-side equilibrium R toward the load
  // + the out-of-block tension traveling down toward the heel/winch side
  const g = lift.geometry;
  const top: Pt = { x: g.topBlock.offsetFromCenterFt, y: g.topBlock.heightFt };
  const load: Pt = { x: g.load.distanceFromTowerFt, y: g.load.heightFt };
  const heel: Pt = { x: g.heelBlock!.offsetFromTowerFt, y: g.heelBlock!.heightFt };

  const uLoad = unit(top, load);
  const uTopHeel = unit(top, heel);
  const vec = sumVectors([
    { T: R, dir: uLoad },
    { T: outBlock, dir: uTopHeel },
  ]);
  const topBlockExact = Math.hypot(vec.x, vec.y);
  const topH = Math.abs(vec.x);
  const topV = Math.abs(vec.y);

  // heel resultant: exact vector sum of the segment tensions through the heel
  const winch: Pt = { x: g.winch.distanceFromTowerFt, y: g.winch.heightFt };
  const uHeelWinch = unit(heel, winch);
  const heelVec = sumVectors([
    { T: haul, dir: uTopHeel },
    { T: winchPull, dir: uHeelWinch },
  ]);
  const heelResultant = Math.hypot(heelVec.x, heelVec.y);
  const anchorH = Math.abs(heelVec.x);
  const anchorV = Math.abs(heelVec.y);
  // FIX: include the heel-block term — the working[] entry shows BOTH terms.
  const baseMoment = topH * g.topBlock.heightFt + anchorH * g.heelBlock!.heightFt;

  const working: WorkingEntry[] = [
    {
      output: 'Dead-end part tension',
      formula: 'T_dead = R / Σ(K^j, j=0..N-1)  (friction makes parts unequal; K from A-13(i) table)',
      substitution: `${R.toFixed(1)} / ${gsum.toFixed(4)}`,
      result: `${deadEnd.toFixed(1)} lb`,
      assumption: `Dead end carries load-side tension; K = A13I_SFF[1][${b}][1] = ${K} per sheave (A-13(i), table-derived).`,
    },
    {
      output: 'Haul side tension',
      formula: 'T_haul = T_dead × K (1 sheave pass)',
      substitution: `${deadEnd.toFixed(1)} × ${K}`,
      result: `${haul.toFixed(1)} lb`,
      assumption: 'T_out = T_in × K per sheave pass (Brian override — friction reeving model).',
    },
    {
      output: 'Tension leaving top block',
      formula: 'T_out = T_haul × K',
      substitution: `${haul.toFixed(1)} × ${K}`,
      result: `${outBlock.toFixed(1)} lb`,
      assumption: 'Hoist side accumulates the K product across all reeved sheave passes.',
    },
    {
      output: 'Required line pull at hoist',
      formula: 'winchPull = T_dead × K^S = T_dead × A13I_SFF[S][b][N] × Σ(K^j)/N',
      substitution: `${deadEnd.toFixed(1)} × K^${S} (S = ${S} total sheave passes incl. heel/fleet block)`,
      result: `${winchPull.toFixed(1)} lb`,
      assumption: `S = TOTAL sheave passes including the heel/fleet block (S=${S}); pull derived from the A-13(i) table (A-13(f) path).`,
    },
    {
      output: 'Top block resultant (EXACT vector sum incl. dead end)',
      formula: '‖Σ T·ûᵢ‖ = R·û_load + T_out·û_heel',
      substitution: `${R.toFixed(1)} lb toward load + ${outBlock.toFixed(1)} lb toward heel`,
      result: `${topBlockExact.toFixed(1)} lb (H = ${topH.toFixed(1)}, V = ${topV.toFixed(1)})`,
      assumption: 'A-1 regression guard: dead-end/out-block tension included in the block reaction — NOT lineLoad×MA÷(n+1).',
    },
    {
      output: 'Heel block resultant under friction reeving',
      formula: '‖Σ T·ûᵢ‖ at heel',
      substitution: `${haul.toFixed(1)} lb (top side) + ${winchPull.toFixed(1)} lb (winch side)`,
      result: `${heelResultant.toFixed(1)} lb`,
      assumption: 'Dead-end tension carried in the heel reaction (A-3 vector resultant, not v1 ×0.5).',
    },
    {
      output: 'Base moment (friction reeving)',
      formula: 'M = H_top×h_top + H_heel×h_heel',
      substitution: `${topH.toFixed(1)}×${g.topBlock.heightFt} + ${anchorH.toFixed(1)}×${g.heelBlock!.heightFt}`,
      result: `${baseMoment.toFixed(1)} ft·lb`,
      assumption: 'Heel anchor lateral × heel height added to the top-block moment.',
    },
  ];

  return {
    ...base,
    forces: {
      ...base.forces,
      linePartTensionLbs: deadEnd,
      winchPullLbs: winchPull,
      topBlockResultantLbs: topBlockExact,
      heelBlockLbs: heelResultant,
      structureReactions: {
        topLateralLbs: topH,
        topVerticalLbs: topV,
        baseMomentFtLbs: baseMoment,
        perAttachment: [
          { name: 'top block', horizontalLbs: topH, verticalLbs: topV, heightFt: g.topBlock.heightFt },
          { name: 'heel block', horizontalLbs: anchorH, verticalLbs: anchorV, heightFt: g.heelBlock!.heightFt },
        ],
      },
    },
    tensions: { deadEndLbs: deadEnd, haulLbs: haul, outBlockLbs: outBlock },
    ideal,
    working,
  };
}

// ---------------------------------------------------------------------------
// a13fMultipliers — A-13(f) load/tag line multipliers (§3.2)
// ---------------------------------------------------------------------------

export function a13fMultipliers(
  thetaDeg: number, alphaDeg: number, _sheaveCount: number, sffPerSheave: number,
): { PM: number; TM: number } {
  // Angle round-up rule (A-13(f) note 1): round up to the nearest 1° (θ) and 5° (α)
  // before chart lookup. Closed form of the chart: PM = 1/(cos θ − sin θ·tan α),
  // TM = PM·sin θ/cos α — reproduces the 8°/65° worked example (PM=1.445, TM=0.476).
  const th = Math.ceil(thetaDeg) * Math.PI / 180;
  const al = Math.ceil(alphaDeg / 5) * 5 * Math.PI / 180;
  const PM = 1 / (Math.cos(th) - Math.sin(th) * Math.tan(al));
  const TM = PM * Math.sin(th) / Math.cos(al);
  void sffPerSheave; void _sheaveCount; // friction handled by hoist pull = P × SFF at call site
  return { PM, TM };
}

// ---------------------------------------------------------------------------
// solveS3 — jump/redirect block (§3.3). Geometry: load → up side A → top
// block → crossing segment → jump block (side B, below top) → down side B →
// winch. Crossing horizontal pull on the top assembly H = T·cos(crossAngle)
// toward side B; jump resultant T·√(2+2·cos ψ); top-block EXACT vector model
// (n parts toward load + dead end toward jump). Torsion: TEMPLATE ONLY —
// arm convention unconfirmed ⇒ R-4 refusal placeholder on the torsion output;
// other outputs compute. (Z11 resolved: NO S3/S5 QE advisory.)
// ---------------------------------------------------------------------------

export interface SolveS3Result extends SolveS1Result {
  crossAngleDeg: number;       // crossing segment angle from horizontal
  H_pullLbs: number;           // horizontal pull on the top assembly toward side B
  jumpResultantLbs: number;    // resultant at the jump block
  angleBetweenDeg: number;     // angle between the segments through the jump block
  topResultantLbs: number;     // EXACT vector resultant at the top block
  netLateralLbs: number;       // net lateral on the tower (flips to side B)
  netLateralDirection: 'A' | 'B';
  baseMomentFtLbs: number;
  torsion: { valueLbs: null; refusal: Refusal };
}

export function solveS3(lift: LiftDetail): SolveS3Result {
  const base = solveS1(lift);
  const g = lift.geometry;
  const h = lift.hardware;
  const T = base.forces.linePartTensionLbs;
  const R = base.resultantR;

  const top: Pt = { x: g.topBlock.offsetFromCenterFt, y: g.topBlock.heightFt };
  const load: Pt = { x: g.load.distanceFromTowerFt, y: g.load.heightFt };
  const jump: Pt = { x: -Math.abs(g.jumpBlock!.offsetFromCenterFt), y: g.jumpBlock!.heightFt }; // side B
  const winch: Pt = { x: -Math.abs(g.winch.distanceFromTowerFt), y: g.winch.heightFt };        // side B

  const uTopJump = unit(top, jump);
  const uTopLoad = unit(top, load);
  const uJumpTop = unit(jump, top);
  const uJumpWinch = unit(jump, winch);

  // crossing segment angle from HORIZONTAL (S3-A fixture: atan(4/6)=33.69°)
  const crossAngle = Math.atan2(Math.abs(uTopJump.y), Math.abs(uTopJump.x)) * 180 / Math.PI;
  // horizontal pull component of the dead-end tension traveling toward the jump block
  const H_pull = T * Math.cos(crossAngle * Math.PI / 180);

  // angle between the two segments through the JUMP block (jump→top, jump→winch)
  const psi = angleBetweenDeg(uJumpTop, uJumpWinch);
  // jump resultant: exact vector sum of the two tensions through the jump block
  const jumpVec = sumVectors([
    { T, dir: uJumpTop },
    { T, dir: uJumpWinch },
  ]);
  const jumpResultant = Math.hypot(jumpVec.x, jumpVec.y);

  // EXACT top-block vector model: n parts toward the load + dead end toward jump
  const vec = sumVectors([
    { T: R, dir: uTopLoad },
    { T, dir: uTopJump },
  ]);
  // net lateral on the tower = signed top-block horizontal from the exact vector
  // sum (positive toward load/side A, negative toward side B) — the jump block
  // pulls the dead end toward side B and can FLIP the lateral direction
  const topH = vec.x;
  const topResultant = Math.hypot(vec.x, vec.y);
  const topV = Math.abs(vec.y);
  const netLateral = topH;
  const netLateralDirection: 'A' | 'B' = netLateral >= 0 ? 'A' : 'B';
  const netLateralMag = Math.abs(netLateral);

  const baseMoment = netLateralMag * g.topBlock.heightFt;

  const working: WorkingEntry[] = [
    ...base.working,
    {
      output: 'Crossing segment angle from horizontal',
      formula: 'crossAngle = atan2(|Δy|, |Δx|) of top→jump segment',
      substitution: `atan2(${Math.abs(uTopJump.x).toFixed(4)}, ${Math.abs(uTopJump.y).toFixed(4)})`,
      result: `${crossAngle.toFixed(2)}°`,
      assumption: 'Derived from stored point coordinates (top block and jump block on opposite faces).',
    },
    {
      output: 'Crossing horizontal pull on top assembly',
      formula: 'H_pull = T·cos(crossAngle)',
      substitution: `${T.toFixed(1)} × cos ${crossAngle.toFixed(2)}°`,
      result: `${H_pull.toFixed(1)} lb`,
      assumption: 'Dead-end part travels toward the jump block on side B; horizontal component (T·cos crossAngle, crossAngle from horizontal) pulls the top assembly toward side B.',
    },
    {
      output: 'Jump block resultant',
      formula: '‖Σ T·ûᵢ‖ = T·û_jump→top + T·û_jump→winch',
      substitution: `${T.toFixed(1)} lb toward top + ${T.toFixed(1)} lb toward winch (ψ between segments ${angleBetweenDeg(uJumpTop, uJumpWinch).toFixed(1)}°)`,
      result: `${jumpResultant.toFixed(1)} lb`,
      assumption: 'Vector resultant at the jump block from segment directions (A-3 fix).',
    },
    {
      output: 'Angle between segments through the jump block',
      formula: 'ψ = angle(u_jump→top, u_jump→winch)',
      substitution: `angleBetween([${uJumpTop.x.toFixed(4)}, ${uJumpTop.y.toFixed(4)}], [${uJumpWinch.x.toFixed(4)}, ${uJumpWinch.y.toFixed(4)}])`,
      result: `${psi.toFixed(2)}°`,
      assumption: 'Angle between the two rope segments meeting at the jump block.',
    },
    {
      output: 'Top block resultant (EXACT vector sum: n parts toward load + dead end toward jump)',
      formula: '‖Σ T·ûᵢ‖ = R·û_load + T·û_jump',
      substitution: `${R.toFixed(1)} lb toward load + ${T.toFixed(1)} lb toward jump`,
      result: `${topResultant.toFixed(1)} lb (H = ${topH.toFixed(1)}, V = ${topV.toFixed(1)})`,
      assumption: 'A-1 exact vector model — segments diverge at the top block, so the conservative (n+1)·T sizing is replaced by the vector sum.',
    },
    {
      output: 'Net lateral on the tower',
      formula: 'net = H_load-side − H_pull (signed, from the exact top-block vector sum)',
      substitution: `(${uTopLoad.x.toFixed(4)}·${R.toFixed(1)} + ${uTopJump.x.toFixed(4)}·${T.toFixed(1)}) = ${topH.toFixed(1)} lb`,
      result: `${netLateralMag.toFixed(1)} lb toward side ${netLateralDirection}`,
      assumption: 'Jump block flips the lateral direction when the crossing pull toward side B exceeds the load-side lateral.',
    },
    {
      output: 'Base moment (jump scenario)',
      formula: 'M = H_net × top height',
      substitution: `${topH.toFixed(1)} × ${g.topBlock.heightFt}`,
      result: `${baseMoment.toFixed(1)} ft·lb`,
      assumption: 'Signed net top lateral × top attachment elevation.',
    },
    {
      output: 'Torsion on the structure (S3 torsion arm)',
      formula: 'REFUSED — R-4 (template geometry unconfirmed)',
      substitution: 'no computation',
      result: 'REFUSED',
      assumption: 'Torsion arm / face-width convention per structure type not yet supplied (Brian input pending Q5). No number is emitted — the torsion output is a refusal placeholder until the geometry conventions are confirmed.',
    },
  ];

  return {
    ...base,
    crossAngleDeg: crossAngle,
    H_pullLbs: H_pull,
    jumpResultantLbs: jumpResultant,
    angleBetweenDeg: psi,
    topResultantLbs: topResultant,
    netLateralLbs: netLateralMag,
    netLateralDirection,
    baseMomentFtLbs: baseMoment,
    torsion: {
      valueLbs: null,
      refusal: {
        rule: 'R-4',
        message: 'S3 torsion output refused: torsion arm / face-width geometry convention not confirmed (Brian input pending). Supply the torsion-arm convention per structure type to enable this output.',
      },
    },
    forces: {
      ...base.forces,
      topBlockResultantLbs: topResultant,
      structureReactions: {
        topLateralLbs: netLateralMag,
        topVerticalLbs: topV,
        baseMomentFtLbs: baseMoment,
        perAttachment: [
          { name: 'top block', horizontalLbs: topH, verticalLbs: topV, heightFt: g.topBlock.heightFt },
          { name: 'jump block', horizontalLbs: Math.abs(jumpVec.x), verticalLbs: Math.abs(jumpVec.y), heightFt: g.jumpBlock!.heightFt },
        ],
      },
    },
    working,
  };
}

// ---------------------------------------------------------------------------
// solveS4 — tied-back head lift (§3.3, Z15 RESOLVED): the tie-back is a
// TETHER/catch device, NOT a tensioned member. The head carries the load line
// (n parts toward the load) plus the dead-end part descending to the winch;
// the tie-back anchor reports a CATCH FORCE (= lateral at head = T·sin of the
// dead-end angle from vertical) — no tie-back WLL comparison, no R-4 refusal
// (R-4 remains ONLY for the S3 torsion output).
// ---------------------------------------------------------------------------

export interface SolveS4Result extends SolveS1Result {
  deadEndAngleDeg: number;      // dead-end segment angle from vertical at the head
  headResultantLbs: number;     // exact vector resultant at the head
  lateralHeadLbs: number;       // lateral component at the head (T·sin deadEndAngle)
  momentAboutTiebackFtLbs: number;
  tieBackCatchLbs: number;      // tether catch force (Z15) — NOT a tensioned-member force
}

export function solveS4(lift: LiftDetail): SolveS4Result {
  const base = solveS1(lift);
  const g = lift.geometry;
  const T = base.forces.linePartTensionLbs;
  const R = base.resultantR;

  const head: Pt = { x: g.topBlock.offsetFromCenterFt, y: g.topBlock.heightFt };
  const load: Pt = { x: g.load.distanceFromTowerFt, y: g.load.heightFt };
  const winch: Pt = { x: g.winch.distanceFromTowerFt, y: g.winch.heightFt };
  const uHeadWinch = unit(head, winch);
  const uHeadLoad = unit(head, load);

  // dead-end angle from vertical
  const deadEndAngle = Math.atan2(Math.abs(uHeadWinch.x), Math.abs(uHeadWinch.y)) * 180 / Math.PI;
  const lateralHead = T * Math.sin(deadEndAngle * Math.PI / 180);

  // EXACT head vector sum: load-side equilibrium R toward the load + dead-end
  // tension down toward the winch (n parts toward the load handled by R)
  const vec = sumVectors([
    { T: R, dir: uHeadLoad },
    { T, dir: uHeadWinch },
  ]);
  const headResultant = Math.hypot(vec.x, vec.y);

  // moment about the tie-back anchor elevation: lateral × (head height − tie-back height)
  const momentAboutTieback = lateralHead * (g.topBlock.heightFt - g.tieBackAnchor!.heightFt);

  // Z15 RESOLVED: tie-back = TETHER catch device. The catch force is the
  // lateral pull at the head that the tether must arrest — NOT a tensioned
  // member force. forces.tieBackLbs stays null (no WLL comparison, no R-4).
  const tieBackCatch = lateralHead;

  const working: WorkingEntry[] = [
    ...base.working,
    {
      output: 'Dead-end angle from vertical at the head',
      formula: 'deadEndAngle = atan2(|Δx|, Δy) of head→winch segment',
      substitution: `atan2(${Math.abs(uHeadWinch.x).toFixed(4)}, ${Math.abs(uHeadWinch.y).toFixed(4)})`,
      result: `${deadEndAngle.toFixed(2)}°`,
      assumption: 'Derived from stored point coordinates (PRD D9 — never typed).',
    },
    {
      output: 'Lateral at the head',
      formula: 'lateral = T·sin(deadEndAngle)',
      substitution: `${T.toFixed(1)} × sin ${deadEndAngle.toFixed(2)}°`,
      result: `${lateralHead.toFixed(1)} lb`,
      assumption: 'Horizontal component of the dead-end part descending to the winch.',
    },
    {
      output: 'Head resultant (EXACT vector sum)',
      formula: '‖Σ T·ûᵢ‖ = R·û_load + T·û_winch',
      substitution: `${R.toFixed(1)} lb toward load + ${T.toFixed(1)} lb toward winch`,
      result: `${headResultant.toFixed(1)} lb`,
      assumption: 'A-1 exact vector model at the head attachment.',
    },
    {
      output: 'Moment about the tie-back anchor',
      formula: 'M = lateral × (head height − tie-back height)',
      substitution: `${lateralHead.toFixed(1)} × (${g.topBlock.heightFt} − ${g.tieBackAnchor!.heightFt})`,
      result: `${momentAboutTieback.toFixed(1)} ft·lb`,
      assumption: 'Lateral at the head × vertical offset to the tie-back anchor elevation.',
    },
    {
      output: 'Tie-back TETHER CATCH FORCE (Z15 resolved)',
      formula: 'catch = lateral at head = T·sin(deadEndAngle)',
      substitution: `${T.toFixed(1)} × sin ${deadEndAngle.toFixed(2)}°`,
      result: `${tieBackCatch.toFixed(1)} lb`,
      assumption: 'The tie-back is a TETHER/catch device, not a tensioned member (Z15). The catch force = lateral pull the tether must arrest; NO tie-back WLL comparison is performed and no R-4 refusal applies to this output (R-4 remains ONLY for S3 torsion).',
    },
  ];

  return {
    ...base,
    deadEndAngleDeg: deadEndAngle,
    headResultantLbs: headResultant,
    lateralHeadLbs: lateralHead,
    momentAboutTiebackFtLbs: momentAboutTieback,
    tieBackCatchLbs: tieBackCatch,
    forces: {
      ...base.forces,
      topBlockResultantLbs: headResultant,
      structureReactions: {
        ...base.forces.structureReactions,
        topLateralLbs: lateralHead,
        topVerticalLbs: Math.abs(vec.y),
        perAttachment: [
          { name: 'head block', horizontalLbs: lateralHead, verticalLbs: Math.abs(vec.y), heightFt: g.topBlock.heightFt },
          { name: 'tie-back tether (catch)', horizontalLbs: tieBackCatch, verticalLbs: 0, heightFt: g.tieBackAnchor!.heightFt },
        ],
      },
    },
    working,
  };
}

// ---------------------------------------------------------------------------
// solveS5 — redirect + mount dual attachment forces (§3.3). The mount (top
// block) carries the load-line equilibrium R toward the load plus the dead-end
// part traveling down to the redirect; the redirect carries that dead-end part
// on to the winch. Redirect anchor vertical reaction is SIGNED (positive =
// net UPWARD). No QE advisory (Z11 resolved).
// ---------------------------------------------------------------------------

export interface SolveS5Result extends SolveS1Result {
  mountResultantLbs: number;
  mountAngleDeg: number;       // from vertical
  redirectResultantLbs: number;
  redirectAngleDeg: number;    // angle between the two segments through the redirect
  anchorHLbs: number;          // redirect anchor horizontal reaction (magnitude)
  anchorVLbs: number;          // redirect anchor vertical reaction, SIGNED (+ = net upward)
  verticalDeltaFt: number;     // mount height − redirect height
}

export function solveS5(lift: LiftDetail): SolveS5Result {
  const base = solveS1(lift);
  const g = lift.geometry;
  const T = base.forces.linePartTensionLbs;
  const R = base.resultantR;

  const mount: Pt = { x: g.mountBlock!.offsetFt, y: g.mountBlock!.heightFt };
  const redirect: Pt = { x: g.redirectBlock!.offsetFt, y: g.redirectBlock!.heightFt };
  const load: Pt = { x: g.load.distanceFromTowerFt, y: g.load.heightFt };
  const winch: Pt = { x: g.winch.distanceFromTowerFt, y: g.winch.heightFt };

  const uMountLoad = unit(mount, load);
  const uMountRedirect = unit(mount, redirect);
  const uRedirectMount = unit(redirect, mount);
  const uRedirectWinch = unit(redirect, winch);

  // MOUNT: load-side equilibrium R toward the load + dead-end part T down to the redirect.
  // The mount's resultant pulls AWAY from the mount in both segment directions.
  const mountVec = sumVectors([
    { T: -R, dir: uMountLoad },
    { T: -T, dir: uMountRedirect },
  ]);
  const mountResultant = Math.hypot(mountVec.x, mountVec.y);
  // mount angle = angle between the two rope segments through the mount
  // (load-side part vs redirect segment) — 33.7° in the S5-A fixture
  const mountAngle = angleBetweenDeg(uMountLoad, uMountRedirect);

  // REDIRECT: two equal tensions T (toward mount, toward winch) — exact vector sum.
  const redirectVec = sumVectors([
    { T, dir: uRedirectMount },
    { T, dir: uRedirectWinch },
  ]);
  const redirectResultant = Math.hypot(redirectVec.x, redirectVec.y);
  const redirectAngle = angleBetweenDeg(uRedirectMount, uRedirectWinch);

  // Redirect ANCHOR reactions: the rope pulls the redirect with redirectVec;
  // the anchor supplies the negative (equal & opposite). SIGNED vertical:
  // positive = net UPWARD force the anchor must resist (rope pulls DOWN on it)
  // — the rope pull on the anchor is +redirectVec applied to the structure.
  const anchorH = Math.abs(redirectVec.x);
  // The rope segments pull the redirect attachment with +redirectVec; the
  // vertical component of that pull is redirectVec.y (negative = downward pull
  // on the anchor... signed convention: anchorV is the NET vertical reaction
  // required, positive = net upward resistance needed).
  const anchorV = -redirectVec.y;
  const verticalDelta = g.mountBlock!.heightFt - g.redirectBlock!.heightFt;

  const working: WorkingEntry[] = [
    ...base.working,
    {
      output: 'Mount resultant (dual attachment: load parts + redirect segment)',
      formula: '‖Σ T·ûᵢ‖ = R·û_load + T·û_redirect (away from mount)',
      substitution: `${R.toFixed(1)} lb toward load + ${T.toFixed(1)} lb toward redirect`,
      result: `${mountResultant.toFixed(1)} lb @ ${mountAngle.toFixed(1)}° from vertical`,
      assumption: 'Exact vector sum at the mount attachment; angle derived from the vector components.',
    },
    {
      output: 'Redirect resultant (two equal tensions T)',
      formula: '‖Σ T·ûᵢ‖ = T·û_redirect→mount + T·û_redirect→winch',
      substitution: `${T.toFixed(1)} lb toward mount + ${T.toFixed(1)} lb toward winch`,
      result: `${redirectResultant.toFixed(1)} lb`,
      assumption: `Angle between the segments through the redirect = ${redirectAngle.toFixed(1)}° (A-3 vector resultant).`,
    },
    {
      output: 'Redirect anchor reactions (H + SIGNED V)',
      formula: 'H = |Σx|, V = −Σy (signed: positive = net UPWARD)',
      substitution: `H = ${Math.abs(redirectVec.x).toFixed(1)}, V = ${(-redirectVec.y).toFixed(1)}`,
      result: `H = ${anchorH.toFixed(1)} lb, V = ${anchorV.toFixed(1)} lb (net upward)`,
      assumption: `Redirect sits ${verticalDelta.toFixed(1)} ft below the mount; the downward-running winch segment gives the anchor a NET UPWARD vertical reaction (reported signed, never magnitudes-only).`,
    },
  ];

  return {
    ...base,
    mountResultantLbs: mountResultant,
    mountAngleDeg: mountAngle,
    redirectResultantLbs: redirectResultant,
    redirectAngleDeg: redirectAngle,
    anchorHLbs: anchorH,
    anchorVLbs: anchorV,
    verticalDeltaFt: verticalDelta,
    forces: {
      ...base.forces,
      topBlockResultantLbs: mountResultant,
      mountBlockLbs: mountResultant,
      redirectBlockLbs: redirectResultant,
      structureReactions: {
        topLateralLbs: Math.abs(mountVec.x),
        topVerticalLbs: Math.abs(mountVec.y),
        baseMomentFtLbs: Math.abs(mountVec.x) * g.mountBlock!.heightFt,
        perAttachment: [
          { name: 'mount block', horizontalLbs: Math.abs(mountVec.x), verticalLbs: Math.abs(mountVec.y), heightFt: g.mountBlock!.heightFt },
          { name: 'redirect block', horizontalLbs: anchorH, verticalLbs: anchorV, heightFt: g.redirectBlock!.heightFt },
        ],
      },
    },
    working,
  };
}

// ---------------------------------------------------------------------------
// D-4: static trolley tag — 2×2 node equilibrium (§3.5, fixture S-TAG-1).
// DECODED GEOMETRY (reverse-fit verified against ALL FOUR Cash values to ±0.5):
//   trolley node = (standoffFt, topBlockHt − standoffFt); static upper anchor
//   2 ft below the top block; hold point at grade. The decoded geometry is an
//   input to solveStaticTag: callers pass the node/anchor points explicitly.
//   The 2×2 solve: ΣFx=0, ΣFy=0 with T·û(top) + S·(û(anchor)+û(hold)) = (0, W).
//   S returns NEGATIVE when the ideal-rope equilibrium requires the hold-side
//   segment to compress — physically the hold-side direction dominates; the
//   MAGNITUDE is the reported tension (fixture convention: S=1835.9 per side).
// ---------------------------------------------------------------------------

export interface StaticTagSolution {
  thetaDeg: number;
  refused: boolean;
  lineTensionLbs: number | null;  // T — null on configuration refusal (R-7)
  staticTensionLbs: number | null; // S — null on configuration refusal (R-7)
  node: Pt;
  working: WorkingEntry[];
  refusals: { rule: string; message: string }[];
  advisories: { code: string; message: string }[];
}

export function solveStaticTag(opts: {
  W: number;
  topBlock: Pt;
  staticUpperAnchor: Pt;
  holdPoint: Pt;
  node: Pt;                     // trolley/load node (EXPLICIT input — D-5 FIX 1;
                                // defaults here are the caller's, not baked in)
  staticLineWllLbs?: number;    // triggers R-5 when S exceeds it
  loadDestinationElevationFt?: number; // R-7: static upper anchor must sit ABOVE it
}): StaticTagSolution {
  const { W, topBlock, staticUpperAnchor: anc, holdPoint: hold, node, staticLineWllLbs, loadDestinationElevationFt } = opts;
  const refusals: { rule: string; message: string }[] = [];
  const advisories: { code: string; message: string }[] = [];
  // PRD static-tag input rules: the static upper anchor must be ABOVE the load
  // destination elevation (refuse otherwise); it is conventionally just below
  // the top block — warn when it sits below it.
  if (loadDestinationElevationFt != null && anc.y <= loadDestinationElevationFt) {
    refusals.push({
      rule: 'R-7',
      message: `Static upper anchor at ${anc.y} ft is not above the load destination elevation ${loadDestinationElevationFt} ft — static-tag input rule (PRD): anchor must sit above where the load will land.`,
    });
  }
  // D-4 FIX 2: configuration refusal ⇒ NO numbers. The R-7 anchor-below-
  // destination case is an input error; returning numeric T/S alongside a
  // refusal violates the refused-paths-emit-no-numbers rule (§3.6).
  if (refusals.length > 0) {
    return { thetaDeg: 0, refused: true, lineTensionLbs: null, staticTensionLbs: null, node, working: [], refusals, advisories };
  }
  // D-4 FIX 4 (defense at the source): coincident-point checks BEFORE any
  // computation — unit() would throw on zero-length segments; refused paths
  // emit no numbers instead (§3.6).
  if (Math.hypot(anc.x - topBlock.x, anc.y - topBlock.y) < 1e-9) {
    refusals.push({
      rule: 'R-7',
      message: 'static upper anchor coincides with the top block — move the anchor to a distinct attachment point',
    });
  }
  if (Math.hypot(anc.x - node.x, anc.y - node.y) < 1e-9) {
    refusals.push({
      rule: 'R-7',
      message: 'static upper anchor coincides with the trolley node — move the anchor to a distinct attachment point',
    });
  }
  if (Math.hypot(hold.x - node.x, hold.y - node.y) < 1e-9) {
    refusals.push({
      rule: 'R-7',
      message: 'hold point coincides with the trolley node — move the hold point to a distinct attachment point',
    });
  }
  if (refusals.length > 0) {
    return { thetaDeg: 0, refused: true, lineTensionLbs: null, staticTensionLbs: null, node, working: [], refusals, advisories };
  }
  if (anc.y < topBlock.y) {
    advisories.push({
      code: 'R-6-ADVISORY',
      message: `Static upper anchor at ${anc.y} ft is ${(topBlock.y - anc.y).toFixed(1)} ft below the top block (${topBlock.y} ft) — check the anchor attachment.`,
    });
  }
  const dT = Math.hypot(topBlock.x - node.x, topBlock.y - node.y);
  const dA = Math.hypot(anc.x - node.x, anc.y - node.y);
  const dH = Math.hypot(hold.x - node.x, hold.y - node.y);
  if (dT < 1e-9 || dA < 1e-9 || dH < 1e-9) {
    // D-4 FIX 3: degenerate geometry is an input error ⇒ refused result, not a
    // throw (refused paths emit no numbers, §3.6).
    refusals.push({
      rule: 'R-7',
      message: 'R-7: zero-length static-tag segment',
    });
    return { thetaDeg: 0, refused: true, lineTensionLbs: null, staticTensionLbs: null, node, working: [], refusals, advisories };
  }
  const uT: Pt = { x: (topBlock.x - node.x) / dT, y: (topBlock.y - node.y) / dT };
  const uA: Pt = { x: (anc.x - node.x) / dA, y: (anc.y - node.y) / dA };
  const uH: Pt = { x: (hold.x - node.x) / dH, y: (hold.y - node.y) / dH };
  const a11 = uT.x, a12 = uA.x + uH.x;
  const a21 = uT.y, a22 = uA.y + uH.y;
  const det = a11 * a22 - a12 * a21;
  if (Math.abs(det) < 1e-10) {
    // Trolley colinear with the static line ⇒ indeterminate within the node model.
    // D-4 FIX 3: refused result, not a throw (refused paths emit no numbers).
    refusals.push({
      rule: 'R-7',
      message: 'S-TAG: static line colinear with load line — move the node off the static-line chord (R-7 degenerate geometry)',
    });
    return { thetaDeg: 0, refused: true, lineTensionLbs: null, staticTensionLbs: null, node, working: [], refusals, advisories };
  }
  const theta = Math.atan2(Math.abs(node.x - topBlock.x), Math.abs(topBlock.y - node.y)) * 180 / Math.PI;
  // [T, S]·A = (0, W)
  const Traw = -a12 * W / det;
  const Sraw = a11 * W / det;
  const T = Traw;
  const S = Math.abs(Sraw);
  if (staticLineWllLbs != null && S > staticLineWllLbs) {
    refusals.push({
      rule: 'R-5',
      message: `Static-line tension ${S.toFixed(1)} lb exceeds the static-line WLL ${staticLineWllLbs} lb — steepen the static line / move the hold point closer.`,
    });
  }
  const working: WorkingEntry[] = [
    {
      output: 'Static-tag node θ (load line from vertical)',
      formula: 'θ = atan2(|Δx|, Δy) of trolley→top-block',
      substitution: `atan2(${Math.abs(node.x - topBlock.x).toFixed(2)} ft, ${Math.abs(topBlock.y - node.y).toFixed(2)} ft)`,
      result: `${theta.toFixed(2)}°`,
      assumption: 'Angle derived from stored point coordinates (PRD D9 — never typed).',
    },
    {
      output: 'Static-tag 2×2 node equilibrium (T, S)',
      formula: 'T·û(top) + S·(û(anchor) + û(hold)) = (0, W_eff)',
      substitution: `W = ${W.toFixed(1)} lb at node (${node.x.toFixed(1)}, ${node.y.toFixed(1)}); ideal trolley block ⇒ same S both sides`,
      result: `T = ${T.toFixed(1)} lb, S = ${S.toFixed(1)} lb per side`,
      assumption: 'Exactly determinate 2-equation node solve (Brian ruling — §10-Q10). Ideal block, no sheave friction on the static line.',
    },
    {
      output: 'Static-line tension magnitude per side',
      formula: 'S = |a11·W / det(A)| from the 2×2 solve',
      substitution: `A = [${a11.toFixed(4)}, ${a12.toFixed(4)}; ${a21.toFixed(4)}, ${a22.toFixed(4)}]`,
      result: `${S.toFixed(1)} lb`,
      assumption: 'S reported as magnitude; the hold-side segment direction dominates when the raw solve is negative. Cash fixture S-TAG-1: T=766.5, S=1835.9 (hold 60 ft); sens: hold 30 ⇒ T=871, S=1092; hold 100 ⇒ S=6400; standoff 3 ft ⇒ T=633.',
    },
  ];
  return { thetaDeg: theta, refused: false, lineTensionLbs: T, staticTensionLbs: S, node, working, refusals, advisories };
}

/** Canonical static-tag node position (decoded from S-TAG-1): node hangs standoffFt below the top block,
 *  at the same horizontal distance out; static upper anchor sits 2 ft below the top block. */
export function staticTagNode(topBlockHt: number, standoffFt: number): Pt {
  return { x: standoffFt, y: topBlockHt - standoffFt };
}

// ---------------------------------------------------------------------------
// D-4: face-basket sling math (§3.4, fixture FACE-BASKET-A)
// ---------------------------------------------------------------------------

export interface FaceBasketSolution {
  alphaDeg: number;             // from VERTICAL (A-6 convention)
  legLbs: number;
  warn: boolean;                // α > 45°
  refusal: { rule: 'R-1'; message: string } | null; // α > 60° ⇒ refuse, NO numbers
  working: WorkingEntry[];
}

export function solveFaceBasket(
  blockForceLbs: number, faceWidthFt: number, slingLengthFt: number,
): FaceBasketSolution {
  const ratio = faceWidthFt / (2 * slingLengthFt);
  if (ratio >= 1) {
    return {
      alphaDeg: 90,
      legLbs: NaN,
      warn: true,
      refusal: {
        rule: 'R-1',
        message: 'Sling legs too wide — each leg carries ≥2× its vertical share. Narrow the hitch. (face width ≥ 2× sling length — basket cannot close)',
      },
      working: [],
    };
  }
  const alpha = Math.asin(ratio) * 180 / Math.PI;
  const leg = blockForceLbs / (2 * Math.cos(alpha * Math.PI / 180));
  const warn = alpha > 45;
  const refuse = alpha > 60;
  const working: WorkingEntry[] = [
    {
      output: 'Face-basket sling angle α (from vertical)',
      formula: 'α = asin(faceWidthFt / (2·slingLengthFt))',
      substitution: `asin(${faceWidthFt} / (2×${slingLengthFt}))`,
      result: `${alpha.toFixed(2)}°`,
      assumption: 'Symmetric 2-sling basket; α measured from VERTICAL (A-6 convention fix). Derived from diagram drag — never typed.',
    },
    {
      output: 'Face-basket sling leg tension',
      formula: 'leg = blockForce / (2·cos α)',
      substitution: `${blockForceLbs} / (2 × cos ${alpha.toFixed(2)}°)`,
      result: `${leg.toFixed(1)} lb`,
      assumption: 'Each leg of the symmetric basket carries half the block force resolved through the leg angle.',
    },
  ];
  return {
    alphaDeg: alpha,
    legLbs: leg,
    warn,
    refusal: refuse
      ? {
          rule: 'R-1',
          message: 'Sling legs too wide — each leg carries ≥2× its vertical share. Narrow the hitch.',
        }
      : null,
    working,
  };
}

// ---------------------------------------------------------------------------
// D-4: refusal suite R-1..R-7 (§3.6 table verbatim; thresholds = Z10 BRIAN
// DEFAULTS: R-1 α>60° refuse / >45° warn; R-5 WLL exceedance; R-6 fleet >5°
// refuse / >2° advisory; R-7 θ≥45° horizontal pull. R-4 template-only refusal
// already exists for S3 torsion.)
// ---------------------------------------------------------------------------

export const REFUSAL_MESSAGES: Record<string, string> = {
  'R-1': 'Sling legs too wide — each leg carries ≥2× its vertical share. Narrow the hitch.',
  'R-5': 'Revise rigging or select higher-capacity equipment',
  'R-6': 'Reposition winch/heel block',
  'R-7': 'Horizontal pull, not hoisting',
};

export interface RefusalCheck {
  checkFleetAngleDeg: (fleetDeg: number) => { rule: 'R-6'; message: string } | null;
  checkThetaDeg: (thetaDeg: number) => { rule: 'R-7'; message: string } | null;
  checkWll: (forceLbs: number, wllLbs: number, component: string) => { rule: 'R-5'; message: string } | null;
  checkSlingAlpha: (alphaDeg: number) => { warn: boolean; refusal: { rule: 'R-1'; message: string } | null };
}

export const refusals: RefusalCheck = {
  // R-6: sheave fleet angle > 5° refuse, > 2° advisory (Z10 BRIAN DEFAULT)
  checkFleetAngleDeg: (fleetDeg) =>
    fleetDeg > 5 ? { rule: 'R-6', message: REFUSAL_MESSAGES['R-6'] } : null,
  // R-7: degenerate geometry θ ≥ 45° (Z10 BRIAN DEFAULT)
  checkThetaDeg: (thetaDeg) =>
    thetaDeg >= 45 ? { rule: 'R-7', message: REFUSAL_MESSAGES['R-7'] } : null,
  // R-5: computed force > WLL (D12: WLL/SF path handled at the call site)
  checkWll: (forceLbs, wllLbs, component) =>
    forceLbs > wllLbs
      ? { rule: 'R-5', message: `${component}: computed force ${forceLbs.toFixed(1)} lb exceeds WLL ${wllLbs} lb — ${REFUSAL_MESSAGES['R-5']}.` }
      : null,
  // R-1: α from vertical — refuse > 60°, warn > 45° (Z10 BRIAN DEFAULT)
  checkSlingAlpha: (alphaDeg) => ({
    warn: alphaDeg > 45,
    refusal: alphaDeg > 60 ? { rule: 'R-1', message: REFUSAL_MESSAGES['R-1'] } : null,
  }),
};

// ---------------------------------------------------------------------------
// D-4: static-tag SEGMENT path (§3.5 SEG-PATH-1) — re-solve per standoff band,
// emit per-segment forces + governing (worst-forces) segment callout.
// ---------------------------------------------------------------------------

export interface SegmentSolution extends SegmentForcesLike {
  note?: string;
}

export interface SegmentForcesLike {
  fromElevationFt: number; toElevationFt: number; standoffFt: number;
  thetaDeg: number; lineTensionLbs: number; staticTensionLbs: number;
  topLateralLbs: number; topVerticalLbs: number; passes: boolean;
}

export function solveStaticTagSegments(opts: {
  W: number;
  topBlockHt: number;
  staticUpperAnchorHt: number;   // anchor height on the tower
  holdPointFt: number;           // ground hold distance from tower
  holdElevationFt?: number;      // usually grade (0)
  segments: { fromElevationFt: number; toElevationFt: number; standoffFt: number; note?: string }[];
  staticLineWllLbs?: number;
  loadDestinationElevationFt?: number; // R-7: static upper anchor must sit ABOVE it
}): { segments: SegmentSolution[]; governingSegmentIndex: number; working: WorkingEntry[]; refusals: { rule: string; message: string }[] } {
  const { W, topBlockHt, staticUpperAnchorHt, holdPointFt, holdElevationFt = 0, segments, staticLineWllLbs, loadDestinationElevationFt } = opts;
  const topBlock: Pt = { x: 0, y: topBlockHt };
  const anchor: Pt = { x: 0, y: staticUpperAnchorHt };
  const refusals: { rule: string; message: string }[] = [];
  // FIX 3: validate band boundaries — overlapping, gapped, or reversed bands
  // are an input error (refuse), not silently accepted.
  const segs = [...segments].sort((a, b) => a.fromElevationFt - b.fromElevationFt || a.toElevationFt - b.toElevationFt);
  for (const s of segs) {
    if (s.toElevationFt <= s.fromElevationFt) {
      refusals.push({
        rule: 'R-7',
        message: `Segment band ${s.fromElevationFt}–${s.toElevationFt} ft is empty or reversed — SEG-PATH bands must run low→high with positive length (§3.5 segment path validation).`,
      });
    }
  }
  for (let i = 1; i < segs.length; i++) {
    const prev = segs[i - 1], cur = segs[i];
    if (cur.fromElevationFt < prev.toElevationFt - 1e-9) {
      refusals.push({
        rule: 'R-7',
        message: `Segment bands overlap: ${prev.fromElevationFt}–${prev.toElevationFt} ft and ${cur.fromElevationFt}–${cur.toElevationFt} ft — SEG-PATH bands must not overlap (§3.5 segment path validation).`,
      });
    } else if (cur.fromElevationFt > prev.toElevationFt + 1e-9) {
      refusals.push({
        rule: 'R-7',
        message: `Segment bands gap from ${prev.toElevationFt} to ${cur.fromElevationFt} ft — SEG-PATH bands must be contiguous (§3.5 segment path validation).`,
      });
    }
  }
  if (refusals.length > 0) {
    return { segments: [], governingSegmentIndex: -1, working: [], refusals };
  }
  // D-4 FIX 2: CONFIGURATION refusals (R-7 anchor below destination, degenerate
  // geometry) suppress numbers for the WHOLE path — no per-segment table.
  // Per-segment R-5 WLL failures KEEP their numbers + passes:false (that is
  // the feature: shows which band governs and by how much).
  const solved: SegmentSolution[] = [];
  for (const seg of segments) {
    // canonical decoded geometry: node hangs standoffFt below the top block,
    // at the same horizontal distance out (S-TAG-1 decode). Defaults pending
    // the D-6 UI wiring — the S-TAG-1 decoded convention; node/anchor/hold
    // become explicit inputs there.
    const node = staticTagNode(topBlockHt, seg.standoffFt);
    const hold: Pt = { x: holdPointFt, y: holdElevationFt };
    // D-4 FIX 3 (defense-in-depth): any unexpected throw from a segment solve
    // becomes a whole-path refusal — never crash the solve mid-loop.
    let sol;
    // D-4 FIX 4: the ENTIRE per-segment body is protected — a throw anywhere
    // (solve, unit()/sumVectors reaction math, push) becomes the same
    // whole-path R-7 refusal; unit() must never throw from a user-reachable
    // path (§3.6 refused paths emit no numbers).
    try {
      sol = solveStaticTag({ W, topBlock, staticUpperAnchor: anchor, holdPoint: hold, node, staticLineWllLbs, loadDestinationElevationFt });
      const configRefusals = sol.refusals.filter((r) => r.rule !== 'R-5');
      if (configRefusals.length > 0) {
        return { segments: [], governingSegmentIndex: -1, working: [], refusals: [...refusals, ...configRefusals] };
      }
      for (const r of sol.refusals) refusals.push(r);
      // top-block reaction: load-line tension toward the node + static-line upper
      // segment pulling toward the anchor (reaction at the tower top block).
      const uTopNode = unit(topBlock, node);
      const uTopAnchor = unit(topBlock, anchor);
      const vec = sumVectors([
        { T: -sol.lineTensionLbs!, dir: uTopNode },
        { T: sol.staticTensionLbs!, dir: uTopAnchor },
      ]);
      solved.push({
        fromElevationFt: seg.fromElevationFt,
        toElevationFt: seg.toElevationFt,
        standoffFt: seg.standoffFt,
        thetaDeg: sol.thetaDeg,
        lineTensionLbs: sol.lineTensionLbs,
        staticTensionLbs: sol.staticTensionLbs,
        topLateralLbs: Math.abs(vec.x),
        topVerticalLbs: Math.abs(vec.y),
        passes: sol.refusals.length === 0,
        note: seg.note,
      });
    } catch (err) {
      return {
        segments: [],
        governingSegmentIndex: -1,
        working: [],
        refusals: [...refusals, { rule: 'R-7', message: `Static-tag segment solve failed: ${err instanceof Error ? err.message : String(err)} (§3.6 refused paths emit no numbers)` }],
      };
    }
  }
  // governing = worst forces: max over BOTH lineTension AND staticTension
  let governing = 0;
  for (let i = 1; i < solved.length; i++) {
    const a = solved[i], b = solved[governing];
    if (Math.max(a.lineTensionLbs, a.staticTensionLbs) > Math.max(b.lineTensionLbs, b.staticTensionLbs)) {
      governing = i;
    }
  }
  const working: WorkingEntry[] = [
    {
      output: 'Segment-based standoff path (per-segment re-solve)',
      formula: 'per segment: solveStaticTag at node = (standoff, topBlock − standoff)',
      substitution: solved.map((s) => `${s.fromElevationFt}–${s.toElevationFt} ft @ ${s.standoffFt} ft ⇒ T=${s.lineTensionLbs.toFixed(1)}, S=${s.staticTensionLbs.toFixed(1)}`).join('; '),
      result: `governing segment #${governing} (${solved[governing].fromElevationFt}–${solved[governing].toElevationFt} ft)`,
      assumption: 'Obstruction pass (tagged-out band) re-solved at its own standoff; governing = worst-forces band, usually the tagged-out segment.',
    },
  ];
  return { segments: solved, governingSegmentIndex: governing, working, refusals };
}

// ---------------------------------------------------------------------------
// D-4: solveS6 — highline + trolley (A-4(b)). ADVISORY-ONLY: fixture S6-A is
// TBD by Brian. Compute and render geometry, but refuse numeric force output
// with a clear reason until the fixture lands (blueprint §3.3 / deferred list).
// ---------------------------------------------------------------------------

export interface SolveS6Result extends SolveS1Result {
  advisoryOnly: true;
  highlineSpanFt: number;
  refusal: Refusal;
}

export function solveS6(lift: LiftDetail): SolveS6Result {
  const g = lift.geometry;
  const hl = g.highline!;
  const spanFt = Math.hypot(
    (g.mountBlock?.offsetFt ?? 0) - hl.shaftAnchor.offsetFt,
    (g.mountBlock?.heightFt ?? g.topBlock.heightFt) - hl.shaftAnchor.heightFt,
  );
  const refusal: Refusal = {
    rule: 'R-4',
    message: 'S6 highline/trolley force model is advisory-only: fixture S6-A is pending (Brian input). Geometry is rendered for planning, but NO numeric force output is provided — forces will compute once the verified fixture lands.',
  };
  // FIX 4: the refusal path must emit NO numeric forces at all — an empty
  // ForceSet (zeros/nulls), advisory copy only. No S1 base solve is performed.
  return {
    engineVersion: '2.0.0',
    derivedAngles: { loadPositionDeg: 0, tagAngleDeg: null, fleetAngleDeg: null, slingAlphaDeg: null },
    resultantR: 0,
    advisoryOnly: true,
    highlineSpanFt: spanFt,
    refusal,
    forces: {
      ...EMPTY_FORCES,
    },
    working: [
      {
        output: 'S6 highline + trolley (A-4(b))',
        formula: 'ADVISORY-ONLY — no numeric fixture (S6-A pending)',
        substitution: `span ≈ ${spanFt.toFixed(1)} ft; trolley at ${hl.trolleyPositionFt} ft from shaft anchor`,
        result: 'REFUSED (advisory)',
        assumption: 'Geometry computed and rendered for planning only. Numeric forces refused until Brian supplies the S6-A fixture values; S6 numbers must never enter refusal/WLL comparisons.',
      },
    ],
    refusals: [refusal],
    advisories: [
      { code: 'S6-ADVISORY', message: 'Guidance only, not a calculated force — S6 highline/trolley model pending verified fixture (S6-A).' },
    ],
    status: 'refused',
  };
}

// ---------------------------------------------------------------------------
// computeLift — main entry (scenario dispatch)
// ---------------------------------------------------------------------------

export function computeLift(lift: LiftDetail): LiftComputed {
  // FIX 2 / FIX 5: shared input validation (§3.6 R-7 + Z8 DLF range) applied to
  // EVERY scenario BEFORE any force solve. A refused scenario emits NO numeric
  // forces (empty ForceSet, status 'refused').
  const validation = validateRiggingConfiguration(lift);
  if (validation.length > 0) {
    return {
      engineVersion: '2.0.0',
      derivedAngles: { loadPositionDeg: 0, tagAngleDeg: null, fleetAngleDeg: null, slingAlphaDeg: null },
      forces: { ...EMPTY_FORCES, jumpBlockLbs: null, staticLineTensionLbs: null, highlineTensionLbs: null },
      tieBackCatchLbs: null,
      working: [
        {
          output: 'Input validation (R-7 degenerate geometry / Z8 DLF range)',
          formula: 'validateRiggingConfiguration',
          substitution: validation.map((v) => v.message).join(' | '),
          result: 'REFUSED — no numeric forces emitted',
          assumption: '§3.6 R-7: degenerate geometry is an input validation error — load at/above top block, θ ≥ 45°, zero-length segment, winch inside tower footprint; Z8: DLF must be 1.0–2.0.',
        },
      ],
      refusals: validation.map((v) => ({ rule: v.rule as Refusal['rule'], message: v.message })),
      advisories: [],
      status: 'refused',
    };
  }
  let result: SolveS1Result;
  switch (lift.scenario) {
    case 'S2-top-heel':
      result = solveS2(lift);
      break;
    case 'S6-highline-trolley': {
      const s6 = solveS6(lift);
      return {
        engineVersion: '2.0.0',
        derivedAngles: s6.derivedAngles,
        forces: {
          linePartTensionLbs: s6.forces.linePartTensionLbs,
          topBlockResultantLbs: s6.forces.topBlockResultantLbs,
          heelBlockLbs: null,
          jumpBlockLbs: null,
          redirectBlockLbs: null,
          mountBlockLbs: s6.forces.mountBlockLbs,
          tieBackLbs: null,
          tagLineLbs: s6.forces.tagLineLbs,
          staticLineTensionLbs: null,
          slingLegLbs: s6.forces.slingLegLbs,
          winchPullLbs: s6.forces.winchPullLbs,
          highlineTensionLbs: null, // ADVISORY-ONLY: never a number until S6-A lands
          structureReactions: s6.forces.structureReactions,
        },
        tieBackCatchLbs: null,
        working: s6.working,
        refusals: s6.refusals.map((r) => ({ ...r, rule: r.rule as Refusal['rule'] })),
        advisories: s6.advisories,
        status: 'refused',
      };
    }
    case 'S4-tied-back':
      result = solveS4(lift);
      break;
    case 'S5-redirect':
      result = solveS5(lift);
      break;
    case 'S1-top-only':
    default:
      result = solveS1(lift);
      break;
  }
  // R-6 fleet angle (S2+): >5° refuse (no numeric forces); 2°<fleet≤5° emits
  // the advisory record (previously silently null).
  const fleetDeg = result.derivedAngles.fleetAngleDeg ?? null;
  if (fleetDeg != null && fleetDeg > 5) {
    return {
      engineVersion: '2.0.0',
      derivedAngles: result.derivedAngles,
      forces: { ...EMPTY_FORCES, jumpBlockLbs: null, staticLineTensionLbs: null, highlineTensionLbs: null },
      tieBackCatchLbs: null,
      working: [
        {
          output: 'Fleet angle R-6 check',
          formula: 'fleet > 5° ⇒ refuse',
          substitution: `fleet angle = ${fleetDeg.toFixed(2)}°`,
          result: 'REFUSED — reposition winch/heel block',
          assumption: 'R-6 (Z10 BRIAN DEFAULT): fleet angle above 5° refuses numeric output.',
        },
      ],
      refusals: [{ rule: 'R-6' as const, message: REFUSAL_MESSAGES['R-6'] }],
      advisories: [],
      status: 'refused',
    };
  }
  const r6Advisories = fleetDeg != null && fleetDeg > 2
    ? [{
        code: 'R-6-ADVISORY',
        message: `Fleet angle ${fleetDeg.toFixed(2)}° exceeds the 2° advisory threshold (R-6) — reposition the winch/heel block if it exceeds 5°.`,
      }]
    : [];
  // Audit #5 (MEDIUM): R-5 WLL wiring for S1–S5 — each computed force is
  // compared against the role-tagged equipment WLLs (hardware.equipment[]).
  // Any exceedance ⇒ R-5 refusal and NO numeric forces (refusal contract).
  if (result.status === 'ok' && lift.hardware.equipment.length > 0) {
    const f = result.forces;
    const roleForce: Record<string, number> = {
      'hoist-line': f.winchPullLbs,
      'tag-line': f.tagLineLbs ?? 0,
      'static-line': 0,
      sling: f.slingLegLbs ?? 0,
      block: Math.max(f.topBlockResultantLbs, f.heelBlockLbs ?? 0, f.redirectBlockLbs ?? 0, f.mountBlockLbs ?? 0),
      shackle: Math.max(f.topBlockResultantLbs, f.heelBlockLbs ?? 0, f.redirectBlockLbs ?? 0, f.mountBlockLbs ?? 0),
    };
    const r5: { rule: 'R-5'; message: string }[] = [];
    for (const eq of lift.hardware.equipment) {
      const force = roleForce[eq.role];
      if (typeof force === 'number' && force > 0 && force > eq.wllLbs) {
        const c = refusals.checkWll(force, eq.wllLbs, `${eq.role} equipment (asset ${eq.assetId})`);
        if (c) r5.push(c);
      }
    }
    if (r5.length > 0) {
      return {
        engineVersion: '2.0.0',
        derivedAngles: result.derivedAngles,
        forces: { ...EMPTY_FORCES, jumpBlockLbs: null, staticLineTensionLbs: null, highlineTensionLbs: null },
        tieBackCatchLbs: null,
        working: [
          {
            output: 'R-5 equipment WLL check',
            formula: 'computed force > equipment WLL ⇒ refuse (no numeric output)',
            substitution: r5.map((x) => x.message).join(' | '),
            result: 'REFUSED — revise rigging or select higher-capacity equipment',
            assumption: 'R-5: utilization above 100% (required ÷ WLL, Z9 — no SF multiplier) refuses numeric output; refusals carry no computed numbers.',
          },
        ],
        refusals: r5,
        advisories: [...result.advisories, ...r6Advisories],
        status: 'refused',
      };
    }
  }
  return {
    engineVersion: '2.0.0',
    derivedAngles: result.derivedAngles,
    forces: {
      linePartTensionLbs: result.forces.linePartTensionLbs,
      topBlockResultantLbs: result.forces.topBlockResultantLbs,
      heelBlockLbs: result.forces.heelBlockLbs,
      jumpBlockLbs: null,
      redirectBlockLbs: result.forces.redirectBlockLbs,
      mountBlockLbs: result.forces.mountBlockLbs,
      tieBackLbs: null,
      tagLineLbs: result.forces.tagLineLbs,
      staticLineTensionLbs: null,
      slingLegLbs: result.forces.slingLegLbs,
      winchPullLbs: result.forces.winchPullLbs,
      highlineTensionLbs: null,
      structureReactions: result.forces.structureReactions,
    },
    working: result.working,
    refusals: [],
    tieBackCatchLbs: 'tieBackCatchLbs' in result ? (result.tieBackCatchLbs as number) : null,
    advisories: [...result.advisories, ...r6Advisories],
    status: result.status,
  };
}
